import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import './servermonitor.css';
import '../backlog/backlog.css';   // shared .sa-filter-btn chips (BO UI Standard)
import { BusyIcon } from '../../components/loading';
import { getToken } from '../../auth/store';
import { fmtLocal } from '../../utils/datetime';
import { useNotify } from '../../components/notifications';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useHotkeys, formatCombo } from '../../components/hotkeys';

// ─────────────────────────────────────────────────────────────────────────
// Server Monitor → History page
// ─────────────────────────────────────────────────────────────────────────
//   • Time range presets (Last hour / Today / Yesterday / 7 days / 30 days / Custom)
//   • Mini line charts: CPU %, Memory %, Memory GB over the selected range
//   • Snapshot table — every captured row
//   • Download buttons: CSV / Excel / PDF
//
// Data source: GET /api/v1/server/history?from=&to=
// All times are UTC on the wire; the page converts to the user's local
// time for display.
// ─────────────────────────────────────────────────────────────────────────

interface SnapshotRow {
  oid: string;
  capturedAt: string;     // ISO UTC
  serverName: string | null;
  cpuPct: number | null;
  memUsedGb: number | null;
  memTotalGb: number | null;
  memPct: number | null;
  diskUsedGb: number | null;
  diskTotalGb: number | null;
  warnings: string | null;
  // The four JSON columns arrive as raw strings — parsed on demand when
  // the user expands a row.  Keeping them as strings makes the list query
  // cheap (no per-row deserialisation) until the user actually drills in.
  iisAppPoolsJson?:     string | null;
  xafRuntimesJson?:     string | null;
  memoryConsumersJson?: string | null;
  servicesJson?:        string | null;
}

interface IisAppPool      { appPool?: string; processId?: number; memoryGb?: number; percentOfTotal?: number; cpuPct?: number; uptime?: string }
interface XafRuntime      { appPool?: string; gen2GcCount?: number; lohSizeMb?: number; threadCount?: number; handleCount?: number; requestsPerSec?: number; errorsPerSec?: number }
interface MemoryConsumer  { name?: string; category?: string; memoryGb?: number; percentOfTotal?: number; processCount?: number }
interface ServiceStatus   { name?: string; isOnline?: boolean; details?: string }

// Leak Scanner (on-demand ClrMD heap scan of a chosen w3wp)
interface LeakTarget   { appPool: string; pid: number; memoryMb: number }
interface LeakType     { typeName: string; count: number; totalMb: number; leakType: string; likelyForms: string; category: string }
interface LeakCategory { name: string; blurb: string; count: number; totalMb: number; pctOfScanned: number; suspect: boolean }
interface LeakResult   { appPool: string; pid: number; scannedAtUtc: string; workingSetMb: number; heapMb: number; objectCount: number; types: LeakType[]; categories: LeakCategory[]; likelyCause?: string | null; note?: string | null }

// Shorten a fully-qualified .NET type name for display: keep the last two
// namespace segments so "DevExpress.ExpressApp.XPObjectSpace" → "ExpressApp.XPObjectSpace"
// and "System.Data.DataTable" → "Data.DataTable" (unique enough, far shorter).
function shortType(name: string): string {
  const parts = name.split('.');
  return parts.length <= 2 ? name : parts.slice(-2).join('.');
}

function safeParse<T>(s: string | null | undefined): T[] {
  if (!s) return [];
  try { const v = JSON.parse(s); return Array.isArray(v) ? v as T[] : []; }
  catch { return []; }
}

// Date/time formatting helpers live in src/utils/datetime.ts so every
// page renders timestamps with one consistent rule (forceUtc + US,
// 24-hour clock in the viewer's local timezone).

interface Summary {
  fromUtc: string;
  toUtc: string;
  sampleCount: number;
  peakCpuPct: number;
  peakCpuAtUtc: string;
  peakMemPct: number;
  peakMemAtUtc: string;
  avgCpuPct: number;
  avgMemPct: number;
  warningCount: number;
  topMemoryConsumer: string | null;
}

type Preset = 'hour' | 'today' | 'yesterday' | '7d' | '30d' | 'custom';

function buildRange(preset: Preset, customFrom?: string, customTo?: string):
    { fromUtc: string; toUtc: string } {
  const now = new Date();
  const isoUtc = (d: Date) => d.toISOString();
  switch (preset) {
    case 'hour':
      return { fromUtc: isoUtc(new Date(now.getTime() - 60 * 60 * 1000)),
               toUtc: isoUtc(now) };
    case 'today': {
      const t = new Date(now); t.setHours(0, 0, 0, 0);
      return { fromUtc: isoUtc(t), toUtc: isoUtc(now) };
    }
    case 'yesterday': {
      const start = new Date(now); start.setDate(start.getDate() - 1); start.setHours(0,0,0,0);
      const end   = new Date(start); end.setDate(end.getDate() + 1);
      return { fromUtc: isoUtc(start), toUtc: isoUtc(end) };
    }
    case '7d':
      return { fromUtc: isoUtc(new Date(now.getTime() - 7  * 24 * 3600 * 1000)),
               toUtc: isoUtc(now) };
    case '30d':
      return { fromUtc: isoUtc(new Date(now.getTime() - 30 * 24 * 3600 * 1000)),
               toUtc: isoUtc(now) };
    case 'custom':
      return { fromUtc: customFrom ? new Date(customFrom).toISOString() : isoUtc(new Date(now.getTime() - 24 * 3600 * 1000)),
               toUtc:   customTo   ? new Date(customTo).toISOString()   : isoUtc(now) };
  }
}

// Tiny SVG line chart — no extra libs.  Takes pre-resolved [x, y] points
// where x is an epoch-ms timestamp.  Renders into a fixed viewBox so the
// SVG scales to whatever width the parent container is.
//
// Axes:
//   • Y axis (left)   — % values at 25 / 50 / 75 / 100
//   • X axis (bottom) — 5 evenly-spaced timestamps showing what the points
//     represent (format adapts: HH:mm for ≤ 1 day, dd-MM HH:mm for ≤ 7 days,
//     dd-MMM for longer ranges).  Users were confused with bare lines.
function MiniLineChart({ points, color, height = 110, max = 100, label }:
    { points: { x: number; y: number }[]; color: string;
      height?: number; max?: number; label?: string }) {
  // Internal coordinate space — SVG scales to fit any parent width via
  // preserveAspectRatio + viewBox.  Pick numbers large enough for smooth
  // curves at any monitor size.
  const VB_W = 1600;
  if (points.length === 0) return (
    <div style={{ width: '100%', height, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#bbb', fontSize: 12,
                  border: '1px dashed #ddd', borderRadius: 4 }}>
      No samples in this range
    </div>);
  const padX = 32, padY = 8;
  const axisH = 16;                 // reserved room at the bottom for X labels
  const plotH = height - axisH;
  const xs = points.map(p => p.x);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const xSpan = xMax - xMin || 1;
  const px = (x: number) => padX + ((x - xMin) / xSpan) * (VB_W - padX - 4);
  const py = (y: number) => plotH - padY - (y / max) * (plotH - padY * 2);
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${px(p.x).toFixed(1)} ${py(p.y).toFixed(1)}`).join(' ');

  // X-axis tick formatter — adapts to the visible range.
  // chart-axis label — permitted §18.6 exception: kept inline, but US locale +
  // 24-hour (hour12:false) and MM-DD order to match the rest of the app
  // (MM/DD/YYYY, never AM/PM).
  const dayMs = 86_400_000;
  const fmtTick = (ms: number) => {
    const d2 = new Date(ms);
    if (xSpan <= dayMs)
      return d2.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    if (xSpan <= 7 * dayMs)
      return `${String(d2.getMonth() + 1).padStart(2, '0')}-${String(d2.getDate()).padStart(2, '0')} ` +
             d2.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    return d2.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
  };
  const tickCount = 5;
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const t = xMin + (i / (tickCount - 1)) * xSpan;
    return { x: t, label: fmtTick(t) };
  });

  return (
    <svg viewBox={`0 0 ${VB_W} ${height}`} preserveAspectRatio="none"
         style={{ display: 'block', width: '100%', height }}>
      {/* Y-axis gridlines at 25/50/75/100% */}
      {[0.25, 0.5, 0.75, 1].map(f => (
        <line key={f} x1={padX} x2={VB_W - 4}
              y1={py(max * f)} y2={py(max * f)}
              stroke="#eef2f6" strokeWidth={1} strokeDasharray="2,2" />
      ))}
      {[0.25, 0.5, 0.75, 1].map(f => (
        <text key={'t' + f} x={4} y={py(max * f) + 3}
              fontSize="9" fill="#999">
          {(max * f).toFixed(0)}{label?.includes('%') ? '%' : ''}
        </text>
      ))}
      <path d={d} fill="none" stroke={color} strokeWidth={1.5}
            strokeLinecap="round" strokeLinejoin="round"
            vectorEffect="non-scaling-stroke" />
      {/* X-axis baseline + ticks */}
      <line x1={padX} x2={VB_W - 4} y1={plotH} y2={plotH}
            stroke="#cbd5e1" strokeWidth={1} />
      {ticks.map((t, i) => (
        <line key={'tk' + i} x1={px(t.x)} x2={px(t.x)}
              y1={plotH} y2={plotH + 3}
              stroke="#94a3b8" strokeWidth={1} />
      ))}
      {/* X-axis labels — anchor first/last inside the plot edges so they
          don't clip; middle ticks center on their tick mark. */}
      {ticks.map((t, i) => (
        <text key={'tl' + i}
              x={px(t.x)} y={height - 3}
              fontSize="9" fill="#64748b"
              textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'}>
          {t.label}
        </text>
      ))}
    </svg>
  );
}

// ── Detail panel rendered inside a snapshot row when expanded ────────
// Shows the same four breakdowns the live Server Monitor page shows:
//   • IIS App Pools      (memory + CPU per pool)
//   • Top Memory Consumers (process / category)
//   • XAF .NET runtimes  (GC, LOH, threads, handles, req/s)
//   • Services           (online / offline)
// All four arrive as JSON strings on the snapshot row and are parsed lazily.
function SnapshotDetail({ row }: { row: SnapshotRow }) {
  const pools     = safeParse<IisAppPool>(row.iisAppPoolsJson);
  const consumers = safeParse<MemoryConsumer>(row.memoryConsumersJson);
  const runtimes  = safeParse<XafRuntime>(row.xafRuntimesJson);
  const services  = safeParse<ServiceStatus>(row.servicesJson);

  const cardStyle: React.CSSProperties = {
    background: 'var(--ai-card)', border: '1px solid var(--ai-line)', borderRadius: 10,
    boxShadow: 'var(--ai-shadow-card)',
    padding: '8px 12px',
  };
  const titleStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: '#1F4E79', marginBottom: 6,
    textTransform: 'uppercase', letterSpacing: 0.4,
  };
  const cellNum: React.CSSProperties = {
    padding: '3px 8px', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', color: '#444',
  };
  const cellTxt: React.CSSProperties = { padding: '3px 8px', textAlign: 'left' };
  const bar = (pct: number, color = '#F97316') => (
    <div style={{ width: 80, height: 5, background: '#eef2f6', borderRadius: 2,
                  display: 'inline-block', verticalAlign: 'middle', marginLeft: 6 }}>
      <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%',
                    background: color, borderRadius: 2 }} />
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                  gap: 10, fontSize: 11 }}>
      {/* IIS App Pools */}
      <div style={cardStyle}>
        <div style={titleStyle}>
          <i className="ti ti-server-2" style={{ marginRight: 4, color: '#2563eb' }} />
          IIS App Pools ({pools.length})
        </div>
        {pools.length === 0 ? (
          <div style={{ color: '#999', fontSize: 11 }}>No IIS pools recorded for this snapshot.</div>
        ) : (
          <table className="smon-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: '#666' }}>
              <th style={cellTxt}>App pool</th>
              <th style={cellNum}>Mem GB</th>
              <th style={cellNum}>% host</th>
              <th style={cellNum}>CPU %</th>
              <th style={cellNum}>PID</th>
            </tr></thead>
            <tbody>
              {pools.slice(0, 12).map((p, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={cellTxt}>{p.appPool ?? '—'}</td>
                  <td style={cellNum}>{(p.memoryGb ?? 0).toFixed(2)}</td>
                  <td style={cellNum}>
                    {(p.percentOfTotal ?? 0).toFixed(1)}%
                    {bar(p.percentOfTotal ?? 0, '#2563eb')}
                  </td>
                  <td style={cellNum}>{(p.cpuPct ?? 0).toFixed(1)}</td>
                  <td style={cellNum}>{p.processId ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Top Memory Consumers */}
      <div style={cardStyle}>
        <div style={titleStyle}>
          <i className="ti ti-chart-donut" style={{ marginRight: 4, color: '#F97316' }} />
          Memory Breakdown — who's using RAM ({consumers.length})
        </div>
        {consumers.length === 0 ? (
          <div style={{ color: '#999', fontSize: 11 }}>No memory consumers recorded.</div>
        ) : (
          <table className="smon-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: '#666' }}>
              <th style={cellTxt}>Process / app pool</th>
              <th style={cellTxt}>Category</th>
              <th style={cellNum}>Memory</th>
              <th style={cellNum}>% host</th>
              <th style={cellNum}>#</th>
            </tr></thead>
            <tbody>
              {consumers.slice(0, 12).map((c, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={cellTxt}>{c.name ?? '—'}</td>
                  <td style={cellTxt}>
                    <span style={{ background: '#f3f4f6', padding: '1px 6px',
                                   borderRadius: 8, color: '#374151', fontSize: 10 }}>
                      {c.category ?? 'Other'}
                    </span>
                  </td>
                  <td style={cellNum}>{(c.memoryGb ?? 0).toFixed(2)} GB</td>
                  <td style={cellNum}>
                    {(c.percentOfTotal ?? 0).toFixed(1)}%
                    {bar(c.percentOfTotal ?? 0, '#F97316')}
                  </td>
                  <td style={cellNum}>{c.processCount ?? 1}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* XAF .NET runtimes */}
      <div style={cardStyle}>
        <div style={titleStyle}>
          <i className="ti ti-code-circle-2" style={{ marginRight: 4, color: '#059669' }} />
          ALPACA XAF runtimes ({runtimes.length})
        </div>
        {runtimes.length === 0 ? (
          <div style={{ color: '#999', fontSize: 11 }}>No XAF runtime counters captured (Windows-only).</div>
        ) : (
          <table className="smon-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: '#666' }}>
              <th style={cellTxt}>App pool</th>
              <th style={cellNum}>Gen2 GC</th>
              <th style={cellNum}>LOH MB</th>
              <th style={cellNum}>Threads</th>
              <th style={cellNum}>Handles</th>
              <th style={cellNum}>req/s</th>
              <th style={cellNum}>err/s</th>
            </tr></thead>
            <tbody>
              {runtimes.map((rt, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={cellTxt}>{rt.appPool ?? '—'}</td>
                  <td style={cellNum}>{rt.gen2GcCount ?? 0}</td>
                  <td style={cellNum}>{(rt.lohSizeMb ?? 0).toFixed(1)}</td>
                  <td style={cellNum}>{rt.threadCount ?? 0}</td>
                  <td style={cellNum}>{rt.handleCount ?? 0}</td>
                  <td style={cellNum}>{(rt.requestsPerSec ?? 0).toFixed(1)}</td>
                  <td style={cellNum}>{(rt.errorsPerSec ?? 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Services */}
      <div style={cardStyle}>
        <div style={titleStyle}>
          <i className="ti ti-server-bolt" style={{ marginRight: 4, color: '#9333ea' }} />
          Services ({services.length})
        </div>
        {services.length === 0 ? (
          <div style={{ color: '#999', fontSize: 11 }}>No services recorded.</div>
        ) : (
          <table className="smon-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {services.map((s, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={cellTxt}>{s.name ?? '—'}</td>
                  <td style={{ ...cellTxt, textAlign: 'right',
                               color: s.isOnline ? '#16a34a' : '#dc2626',
                               fontWeight: 600 }}>
                    {s.isOnline ? '● Online' : '○ Offline'}
                  </td>
                  <td style={{ ...cellTxt, color: '#666', fontSize: 10 }}>
                    {s.details ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function ServerHistoryPage() {
  const notify = useNotify();
  const [preset,     setPreset]     = useState<Preset>('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [rows,       setRows]       = useState<SnapshotRow[]>([]);
  const [summary,    setSummary]    = useState<Summary | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [loadMs,     setLoadMs]     = useState<number | null>(null);
  const [err,        setErr]        = useState<string | null>(null);
  const [capturing,  setCapturing]  = useState(false);
  const [expanded,   setExpanded]   = useState<string | null>(null);   // Oid of expanded row
  // Leak Scanner panel state
  const [leakOpen,    setLeakOpen]    = useState(false);
  const [leakTargets, setLeakTargets] = useState<LeakTarget[]>([]);
  const [leakPid,     setLeakPid]     = useState<number | null>(null);
  const [leakResult,  setLeakResult]  = useState<LeakResult | null>(null);
  const [leakBusy,    setLeakBusy]    = useState(false);

  const range = useMemo(
    () => buildRange(preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    const t0 = performance.now();
    Promise.all([
      api.get('/api/v1/server/history',         { params: range }),
      api.get('/api/v1/server/history/summary', { params: range }),
    ]).then(([listRes, sumRes]) => {
      if (!alive) return;
      setRows((listRes.data as { data?: SnapshotRow[] }).data ?? []);
      setSummary((sumRes.data as { data?: Summary }).data ?? null);
    }).catch(e => {
      if (!alive) return;
      setErr((e as { message?: string }).message ?? 'Failed to load history.');
    }).finally(() => { if (alive) { setLoadMs(performance.now() - t0); setLoading(false); } });
    return () => { alive = false; };
  }, [range.fromUtc, range.toUtc]);

  // Chart points — x = epoch ms (sort key), y = metric value
  const cpuPts = rows.filter(r => r.cpuPct != null)
    .map(r => ({ x: new Date(r.capturedAt).getTime(), y: r.cpuPct! }));
  const memPts = rows.filter(r => r.memPct != null)
    .map(r => ({ x: new Date(r.capturedAt).getTime(), y: r.memPct! }));

  const dl = (path: string) => {
    // Authenticated download — add token to URL via temporary anchor +
    // axios redirect.  Simpler: just open the URL with the Bearer token in
    // the Authorization header by going through axios then synthesising a
    // download blob — but the simplest cross-origin-safe way is to add
    // the token as a query param OR open in a new tab with credentials.
    const token = getToken();
    const url = `${api.defaults.baseURL ?? ''}${path}&from=${encodeURIComponent(range.fromUtc)}&to=${encodeURIComponent(range.toUtc)}`;
    // Fire the request via axios so the bearer token is attached:
    api.get(path.replace(/^\?/, ''), { params: range, responseType: 'blob' })
      .then(res => {
        const blob = new Blob([res.data as BlobPart],
          { type: (res.headers['content-type'] as string) ?? 'application/octet-stream' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        // Filename from Content-Disposition if present, otherwise from URL
        const cd = res.headers['content-disposition'] as string | undefined;
        const fnMatch = cd?.match(/filename=("?)([^";]+)\1/i);
        link.download = fnMatch?.[2] ?? path.split('/').pop() ?? 'download';
        document.body.appendChild(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(link.href), 5000);
      })
      .catch(e => setErr((e as { message?: string }).message ?? 'Download failed.'));
    void url; void token;
  };

  const captureNow = () => {
    setCapturing(true); setErr(null);
    api.post('/api/v1/server/history/capture-now')
      .then(() => {
        // Refresh to show the just-captured row + flash a success toast.
        // We also force preset to 'hour' if the user is on a range that
        // wouldn't show the new row (e.g. 'yesterday'), so the snapshot
        // they just took is immediately visible.
        return Promise.all([
          api.get('/api/v1/server/history',         { params: range }),
          api.get('/api/v1/server/history/summary', { params: range }),
        ]).then(([listRes, sumRes]) => {
          setRows((listRes.data as { data?: SnapshotRow[] }).data ?? []);
          setSummary((sumRes.data as { data?: Summary }).data ?? null);
          notify.success('Snapshot captured.');
        });
      })
      .catch(e => notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message
        ?? (e as { message?: string }).message
        ?? 'Capture failed.'))
      .finally(() => setCapturing(false));
  };

  // Leak Scanner — open the panel and load the scannable app-pool list.
  const openLeakScanner = () => {
    const next = !leakOpen;
    setLeakOpen(next);
    if (next && leakTargets.length === 0) {
      api.get('/api/v1/server/history/leak-targets')
        .then(res => {
          const list = (res.data as { data?: LeakTarget[] }).data ?? [];
          setLeakTargets(list);
          // Default to the biggest pool (most likely the leaker).
          const first = list[0];
          if (first) setLeakPid(first.pid);
        })
        .catch(e => notify.error((e as { message?: string }).message ?? 'Could not list app pools.'));
    }
  };

  // Run the on-demand heap scan for the selected pool.
  const runLeakScan = () => {
    if (leakPid == null) { notify.warning('Pick an app pool to scan first.'); return; }
    setLeakBusy(true); setLeakResult(null);
    api.post(`/api/v1/server/history/leak-scan?pid=${leakPid}`)
      .then(res => {
        const r = (res.data as { data?: LeakResult }).data ?? null;
        setLeakResult(r);
        if (r?.note) notify.info(r.note);
        else notify.success('Heap scan complete.');
      })
      .catch(e => notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
        ?? (e as { message?: string }).message ?? 'Scan failed.'))
      .finally(() => setLeakBusy(false));
  };

  // Shared-grid columns for the snapshot history.  A leading chevron column
  // mirrors the expand/collapse affordance; CPU%/Mem% keep their ≥80% red
  // highlight via a custom renderer (text mirrors the value for filter/sort).
  const expandedRow = useMemo(() => rows.find(r => r.oid === expanded) ?? null, [rows, expanded]);
  const histCols: AppColumn<SnapshotRow>[] = [
    { field: '_exp', header: '', width: 28, align: 'center', sortable: false, filterable: false, noExport: true,
      text: () => '',
      render: r => <i className={expanded === r.oid ? 'ti ti-chevron-down' : 'ti ti-chevron-right'} style={{ color: '#666' }} /> },
    { field: 'capturedAt', header: 'Captured', type: 'datetime', width: 180,
      render: r => fmtLocal(r.capturedAt), text: r => fmtLocal(r.capturedAt) },
    { field: 'cpuPct', header: 'CPU %', type: 'number', width: 90,
      value: r => r.cpuPct ?? 0,
      render: r => <span style={{ color: (r.cpuPct ?? 0) >= 80 ? '#dc2626' : '#222' }}>{(r.cpuPct ?? 0).toFixed(1)}%</span>,
      text: r => `${(r.cpuPct ?? 0).toFixed(1)}%` },
    { field: 'memPct', header: 'Mem %', type: 'number', width: 90,
      value: r => r.memPct ?? 0,
      render: r => <span style={{ color: (r.memPct ?? 0) >= 80 ? '#dc2626' : '#222', fontWeight: (r.memPct ?? 0) >= 80 ? 700 : 400 }}>{(r.memPct ?? 0).toFixed(1)}%</span>,
      text: r => `${(r.memPct ?? 0).toFixed(1)}%` },
    { field: 'memUsedGb', header: 'Mem (GB)', type: 'number', width: 120,
      value: r => r.memUsedGb ?? 0,
      render: r => <>{(r.memUsedGb ?? 0).toFixed(2)} / {(r.memTotalGb ?? 0).toFixed(2)}</>,
      text: r => `${(r.memUsedGb ?? 0).toFixed(2)} / ${(r.memTotalGb ?? 0).toFixed(2)}` },
    { field: 'diskUsedGb', header: 'Disk (GB)', type: 'number', width: 120,
      value: r => r.diskUsedGb ?? 0,
      render: r => <>{(r.diskUsedGb ?? 0).toFixed(0)} / {(r.diskTotalGb ?? 0).toFixed(0)}</>,
      text: r => `${(r.diskUsedGb ?? 0).toFixed(0)} / ${(r.diskTotalGb ?? 0).toFixed(0)}` },
    { field: 'warnings', header: 'Warnings',
      render: r => <span style={{ color: '#9a3412' }}>{r.warnings ?? ''}</span>,
      text: r => r.warnings ?? '' },
  ];

  // Time-range presets — shared .sa-filter-btn chips (same treatment as the
  // Audit Trail operation chips; theme-token active state, no per-form style).
  const presetBtn = (key: Preset, label: string) => (
    <button key={key} onClick={() => setPreset(key)}
            className={`sa-filter-btn${preset === key ? ' active' : ''}`}>
      {label}
    </button>
  );

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the two
  // form-specific toolbar actions, mirroring each button.  Alt+S = Snapshot now
  // (mirrors its `capturing` disabled) · Alt+K = Leak Scanner.
  useHotkeys([
    { combo: 'alt+s', label: 'Snapshot now', group: 'Server History',
      run: () => captureNow(), disabled: capturing },
    { combo: 'alt+k', label: 'Leak Scanner', group: 'Server History',
      run: () => openLeakScanner() },
    { combo: 'alt+shift+x', label: 'Excel', group: 'Server History',
      run: () => dl('/api/v1/server/history/export.xlsx') },
    { combo: 'alt+shift+p', label: 'PDF', group: 'Server History',
      run: () => dl('/api/v1/server/history/export.pdf') },
  ]);

  return (
    // Same shell-style as the live Server Monitor page: takes the available
    // height inside the form slot, owns its own scrollbar, normal padding —
    // sits right next to the navigation panel, not edge-to-edge.
    <div
      className="sh-scroll"
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: 'var(--ai-bg)',
        fontFamily: 'var(--ai-font)',
      }}
    >
      <style>{`
        .sh-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
        .sh-scroll::-webkit-scrollbar-track { background: transparent; }
        .sh-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 5px; }
        .sh-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    <div style={{ padding: '14px 20px' }}>
      <FormTitle pageId="servermon-history" loadMs={loadMs} loading={loading} />

      {/* Shared toolbar — read-only dashboard (no CRUD).  Time-range presets,
          custom-range inputs and the capture / Excel / PDF download buttons live
          in the left slot (these are form-specific); the primary CSV download is
          the canonical "Export to".  No Refresh — the page reloads on range
          change. */}
      <div style={{ marginBottom: 12 }}>
        <AppToolbar
          left={
            <>
              {presetBtn('hour',      'Last hour')}
              {presetBtn('today',     'Today')}
              {presetBtn('yesterday', 'Yesterday')}
              {presetBtn('7d',        'Last 7 days')}
              {presetBtn('30d',       'Last 30 days')}
              {presetBtn('custom',    'Custom')}
              {preset === 'custom' && (
                <>
                  <input type="datetime-local" value={customFrom}
                         onChange={e => setCustomFrom(e.target.value)}
                         style={{ padding: '3px 6px', fontSize: 12,
                                  border: '1px solid var(--ai-line)', borderRadius: 3 }} />
                  <span>→</span>
                  <input type="datetime-local" value={customTo}
                         onChange={e => setCustomTo(e.target.value)}
                         style={{ padding: '3px 6px', fontSize: 12,
                                  border: '1px solid var(--ai-line)', borderRadius: 3 }} />
                </>
              )}
              <span style={{ flex: 1 }} />
              {/* Shared .ca-btn actions (BO UI Standard — no per-form button
                  colours); existing capture/export logic unchanged. */}
              <button className={`ca-btn${leakOpen ? ' active' : ''}`} onClick={openLeakScanner}
                      title={`Leak Scanner (${formatCombo('alt+k')})`}>
                <i className="ti ti-bug" />&nbsp;Leak Scanner
              </button>
              <button className="ca-btn" onClick={captureNow} disabled={capturing}
                      title={`Snapshot now (${formatCombo('alt+s')})`}>
                <BusyIcon busy={capturing} icon="ti-camera" />
                &nbsp;{capturing ? 'Capturing…' : 'Snapshot now'}
              </button>
              <button className="ca-btn" onClick={() => dl('/api/v1/server/history/export.xlsx')}
                      title={`Excel (${formatCombo('alt+shift+x')})`}>
                <i className="ti ti-file-spreadsheet" />&nbsp;Excel
              </button>
              <button className="ca-btn" onClick={() => dl('/api/v1/server/history/export.pdf')}
                      title={`PDF (${formatCombo('alt+shift+p')})`}>
                <i className="ti ti-file-type-pdf" />&nbsp;PDF
              </button>
            </>
          }
          exportData={{ filename: 'server-history', ...gridExportMatrix(rows, histCols) }}
        />
      </div>

      {/* Leak Scanner panel — on-demand managed-heap scan of a chosen w3wp.
          Shows which .NET object types are piling up inside the target process
          (e.g. legacy ALPACA LIMS) and the LIKELY source form for each known
          leak type — without any code change in that app. */}
      {leakOpen && (
        <div style={{ background: 'var(--ai-card)', border: '1px solid var(--ai-line)',
                      borderRadius: 10, boxShadow: 'var(--ai-shadow-card)',
                      padding: '12px 16px', marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1F4E79', marginBottom: 4 }}>
            <i className="ti ti-bug" style={{ marginRight: 5, color: '#dc2626' }} />
            Memory Leak Scanner
          </div>
          <div style={{ fontSize: 11.5, color: '#666', marginBottom: 10, lineHeight: 1.5 }}>
            Pick an IIS application and scan its live memory to see which object types are
            not being released — each is mapped to the form(s) that usually create it. This
            reads the target process directly (no change to that app) and briefly touches it,
            so run it on demand. If a scan can’t attach, run the Alpaca Intelligence app pool
            as LocalSystem/admin.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#444' }}>Application:</span>
            <select value={leakPid ?? ''} onChange={e => setLeakPid(Number(e.target.value))}
                    style={{ padding: '4px 8px', fontSize: 12, border: '1px solid var(--ai-line)',
                             borderRadius: 4, minWidth: 260 }}>
              {leakTargets.length === 0 && <option value="">No IIS worker processes found</option>}
              {leakTargets.map(t => (
                <option key={t.pid} value={t.pid}>
                  {t.appPool} — {t.memoryMb.toFixed(0)} MB (PID {t.pid})
                </option>
              ))}
            </select>
            <button className="ca-btn" onClick={runLeakScan} disabled={leakBusy || leakPid == null}>
              <BusyIcon busy={leakBusy} icon="ti-scan" />
              &nbsp;{leakBusy ? 'Scanning…' : 'Scan now'}
            </button>
          </div>

          {leakResult && (
            <>
              {/* Total process memory (the "7 GB") shown prominently, with the
                  managed-heap portion the scan actually walked underneath it. */}
              <div style={{ display: 'flex', gap: 18, alignItems: 'flex-end', flexWrap: 'wrap',
                            margin: '2px 0 8px' }}>
                <div>
                  <div style={{ fontSize: 10.5, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    Total memory · {leakResult.appPool}
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1, color: '#111' }}>
                    {(leakResult.workingSetMb / 1024).toFixed(2)} GB
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#6b7280' }}>
                      {' '}({leakResult.workingSetMb.toLocaleString(undefined, { maximumFractionDigits: 0 })} MB)
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: 11.5, color: '#6b7280', paddingBottom: 3 }}>
                  Managed heap scanned:{' '}
                  <b style={{ color: '#374151' }}>
                    {(leakResult.heapMb / 1024).toFixed(2)} GB ({leakResult.heapMb.toFixed(0)} MB)
                  </b>{' · '}
                  {leakResult.objectCount.toLocaleString()} objects · {fmtLocal(leakResult.scannedAtUtc)}
                </div>
              </div>
              {leakResult.note && (
                <div style={{ fontSize: 11, color: '#9a3412', marginBottom: 6 }}>{leakResult.note}</div>
              )}
              {/* Likely cause — the one-line answer to "what is this memory for?" */}
              {leakResult.likelyCause && (
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderLeft: '4px solid #ea580c',
                              borderRadius: 6, padding: '8px 12px', margin: '2px 0 10px', fontSize: 12.5, color: '#7c2d12' }}>
                  <b>Likely cause:</b> {leakResult.likelyCause}
                </div>
              )}

              {/* Where the memory went — plain-English buckets so a non-expert can read the heap. */}
              {leakResult.categories && leakResult.categories.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 6 }}>Where the memory went</div>
                  {leakResult.categories.map((c, i) => {
                    const pct = Math.max(0, Math.min(100, c.pctOfScanned));
                    return (
                      <div key={i} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12 }}>
                          <span style={{ fontWeight: 600 }}>
                            {c.suspect && <span title="Likely leak source" style={{ color: '#ea580c' }}>● </span>}
                            {c.name}
                          </span>
                          <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums', color: '#374151' }}>
                            {c.totalMb.toFixed(1)} MB · {pct.toFixed(1)}% · {c.count.toLocaleString()} objects
                          </span>
                        </div>
                        <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', margin: '3px 0' }}>
                          <div style={{ width: `${pct}%`, height: '100%',
                                        background: c.suspect ? '#ea580c' : '#94a3b8' }} />
                        </div>
                        <div style={{ fontSize: 10.5, color: '#6b7280' }}>{c.blurb}</div>
                      </div>
                    );
                  })}
                  <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 4 }}>
                    <span style={{ color: '#ea580c' }}>●</span> = likely leak source (real app/data objects). Grey =
                    text &amp; collections that merely back them. Percentages are of the {leakResult.heapMb.toFixed(0)} MB scanned.
                  </div>
                </div>
              )}

              {/* Detail: the raw top types (for engineers) */}
              <details open={!leakResult.likelyCause}>
                <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 12.5, marginBottom: 6 }}>
                  Object type detail ({leakResult.types.length})
                </summary>
              {leakResult.types.length === 0 ? (
                <div style={{ color: '#999', fontSize: 11 }}>No object types returned.</div>
              ) : (
                <table className="smon-tbl" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                  <thead><tr style={{ color: '#666', textAlign: 'left' }}>
                    <th style={{ padding: '4px 8px' }}>Object type</th>
                    <th style={{ padding: '4px 8px', textAlign: 'right' }}>Count</th>
                    <th style={{ padding: '4px 8px', textAlign: 'right' }}>Memory</th>
                    <th style={{ padding: '4px 8px' }}>Leak</th>
                    <th style={{ padding: '4px 8px' }}>Likely source form(s)</th>
                  </tr></thead>
                  <tbody>
                    {leakResult.types.map((t, i) => {
                      const known = t.leakType !== '';
                      const badge = { A: '#b3261e', B: '#8a5a00', C: '#7a1fa2', D: '#0b6b6b' }[t.leakType] ?? '#9ca3af';
                      return (
                        <tr key={i} style={{ borderTop: '1px solid #f3f4f6',
                                             background: known ? '#fff7f5' : undefined }}>
                          <td style={{ padding: '3px 8px', fontFamily: 'monospace', fontSize: 10.5 }}>
                            {shortType(t.typeName)}
                          </td>
                          <td style={{ padding: '3px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {t.count.toLocaleString()}
                          </td>
                          <td style={{ padding: '3px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                       fontWeight: t.totalMb >= 100 ? 700 : 400 }}>
                            {t.totalMb.toFixed(1)} MB
                          </td>
                          <td style={{ padding: '3px 8px' }}>
                            {known && (
                              <span style={{ background: badge, color: '#fff', borderRadius: 8,
                                             padding: '0 7px', fontSize: 10, fontWeight: 700 }}>
                                {t.leakType}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '3px 8px', color: known ? '#7a1e1e' : '#9ca3af' }}>
                            {t.likelyForms || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              <div style={{ fontSize: 10.5, color: '#888', marginTop: 6, lineHeight: 1.5 }}>
                Rows tinted red match a known leak pattern from the ALPACA LIMS audit. A high, growing
                count for these types (re-scan to compare) means those objects are being created and not
                released by the listed form(s). The form mapping is a hint — confirm with the audit’s
                file/line list. Types without a mapping are shown for context only.
              </div>
              </details>
            </>
          )}
        </div>
      )}

      {/* Error banner */}
      {err && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca',
                      color: '#991b1b', padding: '8px 12px', borderRadius: 4,
                      fontSize: 12, marginBottom: 12 }}>
          {err}
        </div>
      )}
      {/* Summary card */}
      {summary && (
        <div style={{ background: 'var(--ai-card)', border: '1px solid var(--ai-line)',
                      borderRadius: 10, boxShadow: 'var(--ai-shadow-card)',
                      padding: '12px 16px', marginBottom: 14,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: 12, fontSize: 12 }}>
          <div>
            <div style={{ color: '#666' }}>Samples</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{summary.sampleCount}</div>
          </div>
          <div>
            <div style={{ color: '#666' }}>Peak CPU</div>
            <div style={{ fontSize: 18, fontWeight: 700,
                          color: summary.peakCpuPct >= 80 ? '#dc2626' : '#111' }}>
              {summary.peakCpuPct.toFixed(1)}%
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>
              at {fmtLocal(summary.peakCpuAtUtc)}
            </div>
          </div>
          <div>
            <div style={{ color: '#666' }}>Peak Memory</div>
            <div style={{ fontSize: 18, fontWeight: 700,
                          color: summary.peakMemPct >= 80 ? '#dc2626' : '#111' }}>
              {summary.peakMemPct.toFixed(1)}%
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>
              at {fmtLocal(summary.peakMemAtUtc)}
            </div>
          </div>
          <div>
            <div style={{ color: '#666' }}>Avg CPU / Mem · Warnings</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {summary.avgCpuPct.toFixed(1)}% / {summary.avgMemPct.toFixed(1)}% ·{' '}
              <span style={{ color: summary.warningCount > 0 ? '#dc2626' : '#666' }}>
                {summary.warningCount} warnings
              </span>
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>
              Top consumer: {summary.topMemoryConsumer ?? '—'}
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div style={{ background: 'var(--ai-card)', border: '1px solid var(--ai-line)',
                    borderRadius: 10, boxShadow: 'var(--ai-shadow-card)',
                    padding: '12px 16px', marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1F4E79', marginBottom: 4 }}>
          CPU % over time
        </div>
        <MiniLineChart points={cpuPts} color="#2563eb" max={100} label="%" />
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1F4E79', marginTop: 14, marginBottom: 4 }}>
          Memory % over time
        </div>
        <MiniLineChart points={memPts} color="#ea580c" max={100} label="%" />
      </div>

      {/* Snapshot table — shared <AppDataGrid> (styling, per-column filters,
          sort, footer / pagination, load-time all standardised).  Click a row
          to expand the per-snapshot breakdown shown directly below the grid. */}
      <div style={{ background: 'var(--ai-card)', border: '1px solid var(--ai-line)',
                    borderRadius: 10, boxShadow: 'var(--ai-shadow-card)', overflow: 'hidden' }}>
        <AppDataGrid<SnapshotRow>
          rows={rows}
          columns={histCols}
          getRowId={r => r.oid}
          loading={loading}
          loadMs={loadMs}
          selectedRowId={expanded}
          onRowClick={r => setExpanded(expanded === r.oid ? null : r.oid)}
          emptyText='No snapshots in this range yet. The capture worker runs every hour; click "Snapshot now" for an immediate one.'
        />
        {expandedRow && (
          <div style={{ padding: '8px 14px 14px 14px', background: 'var(--ai-header-bg)',
                        borderTop: '1px solid var(--ai-line)' }}>
            <SnapshotDetail row={expandedRow} />
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
